import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { Bell, Check, CheckCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

interface Notif {
  id: string;
  title: string;
  body: string;
  category: string;
  read_at: string | null;
  created_at: string;
  user_id: string | null;
}

const NotificationsPage = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState<Notif[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { if (!authLoading && !user) navigate("/auth"); }, [authLoading, user, navigate]);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    const load = async () => {
      const { data } = await supabase
        .from("notifications").select("*")
        .order("created_at", { ascending: false }).limit(200);
      if (!cancelled) { setItems((data || []) as any); setLoading(false); }
    };
    load();
    const ch = supabase.channel("notifs")
      .on("postgres_changes", { event: "*", schema: "public", table: "notifications" }, load)
      .subscribe();
    return () => { cancelled = true; supabase.removeChannel(ch); };
  }, [user]);

  const markAll = async () => {
    if (!user) return;
    await supabase.from("notifications").update({ read_at: new Date().toISOString() })
      .eq("user_id", user.id).is("read_at", null);
  };

  const markOne = async (id: string) => {
    await supabase.from("notifications").update({ read_at: new Date().toISOString() }).eq("id", id);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container mx-auto px-4 pt-24 pb-12">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
              <Bell className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-foreground">الإشعارات</h1>
              <p className="text-sm text-muted-foreground">إشعارات الحساب وتنبيهات الأسعار</p>
            </div>
          </div>
          <Button variant="outline" onClick={markAll} className="gap-2">
            <CheckCheck className="w-4 h-4" /> تعليم الكل كمقروء
          </Button>
        </div>

        {loading ? (
          <div className="text-center py-12 text-muted-foreground">جاري التحميل...</div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center py-20 text-muted-foreground">
            <Bell className="w-12 h-12 mb-3 opacity-40" />
            <p>لا توجد إشعارات بعد</p>
          </div>
        ) : (
          <div className="space-y-2">
            {items.map((n) => {
              const unread = !n.read_at;
              return (
                <div key={n.id} className={`p-4 rounded-xl border ${unread ? "border-primary/40 bg-primary/5" : "border-border bg-secondary/30"}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-foreground">{n.title}</h3>
                        <Badge variant="outline" className="text-[10px]">{n.category}</Badge>
                        {unread && <span className="w-2 h-2 rounded-full bg-primary" />}
                      </div>
                      <p className="text-sm text-muted-foreground whitespace-pre-wrap">{n.body}</p>
                      <p className="text-[10px] text-muted-foreground mt-2">{new Date(n.created_at).toLocaleString()}</p>
                    </div>
                    {unread && n.user_id && (
                      <Button size="sm" variant="ghost" onClick={() => markOne(n.id)}>
                        <Check className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
};

export default NotificationsPage;
